import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CurrencyInput from './CurrencyInput'; 
import { UNMANAGED_ACCOUNT_ID, PAYMENTS_MOD } from './Constants';
import AccountSelectorList from './AccountSelectorList';
import {authFetch} from '../utils/api';

const formatCurrency = (amount) => {
    return Number(amount).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const UNMANAGED_ACCOUNT = {
    id: UNMANAGED_ACCOUNT_ID, // -99
    name: "Aucun compte n'est à renseigner", 
    personId: -1, 
    isUnmanagedOption: true, 
};

const styles = {
    modalOverlay: {
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: "#fff", padding: "25px", borderRadius: "8px", width: "650px", maxHeight: '90vh', overflowY: 'auto'
    },
    form: { display: "flex", flexDirection: "column", gap: "10px" },
    input: { padding: "10px", borderRadius: "4px", border: "1px solid #ccc", },
    modalActions: { display: "flex", justifyContent: "flex-end", marginTop: "15px", gap: "10px", },
    reimburseButton: { backgroundColor: '#4CAF50', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
    cancelButton: { backgroundColor: '#6c757d', color: '#fff', padding: '10px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    modalWarning: { backgroundColor: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', padding: '10px', borderRadius: '4px', marginBottom: '15px', textAlign: 'center', fontSize: '0.9em' },
    select: { padding: "10px", borderRadius: "4px", border: "1px solid #ccc", },
    accountSelectorsContainer: { display: 'flex', gap: '20px', marginTop: '15px' },
    accountSelectorColumn: { flex: 1 },
};


export default function ReimbursementModal({ show, debtCreditItems, totalAmount, currentUser, persons, accounts, onClose, selectedPerson, selectedAccount }) {
    const [reimbursementData, setReimbursementData] = useState({
        // ✅ CORRECTION : Vérifie si debtCreditItems existe et utilise 0 si c'est le cas.
        amount: (debtCreditItems || [])
            .reduce((sum, item) => sum + (Number(item.remaining_amount) || 0), 0)
            .toFixed(2),
        date: new Date().toISOString().substring(0, 10),
        reimbursementMode: 'Virement',
    });

    const [selectedSourceId, setSelectedSourceId] = useState(null);
    const [selectedDestinationId, setSelectedDestinationId] = useState(null);

    const nonBancaireModes = ["Espèces", "Tickets restaurant", "Autre"];
    const isBancaireMode = !nonBancaireModes.includes(reimbursementData.reimbursementMode);
    
    // Détermine si l'utilisateur a sélectionné plusieurs lignes.
    const isMultiSelection = debtCreditItems && debtCreditItems.length > 1;

    // Comptes disponibles (avec le nom de la personne pour l'affichage)
    const managedAccountsWithPersonName = accounts.map(acc => ({ 
        ...acc, 
        personName: persons.find(p => p.id === acc.personId)?.name || 'N/A'
    }));

    let allAccounts = managedAccountsWithPersonName;

    if (!allAccounts.some(acc => acc.id === UNMANAGED_ACCOUNT_ID)) {
        allAccounts = [...allAccounts, UNMANAGED_ACCOUNT];
    }

    // 1. Déterminer les personnes impliquées
    const allDebtorIds = Array.from(new Set(debtCreditItems.map(item => item.debtor_person_id)));
    const allCreditorIds = Array.from(new Set(debtCreditItems.map(item => item.creditor_person_id)));

    // Calcul de la liste de toutes les personnes impliquées
    const allInvolvedIds = Array.from(new Set([...allDebtorIds, ...allCreditorIds]));

    // Détermine si la sélection est simple (pour compensation)
    const isSimpleReimbursement = allInvolvedIds.length === 2; 

    // ✅ Stabilisation de la liste complète des comptes (y compris l'option UNMANAGED)
    const stabilizedAllAccounts = useMemo(() => {
        const managedAccountsWithPersonName = accounts.map(acc => ({ 
            ...acc, 
            personName: persons.find(p => p.id === acc.personId)?.name || 'N/A'
        }));
    
        let finalAccounts = managedAccountsWithPersonName;
    
        if (!finalAccounts.some(acc => acc.id === UNMANAGED_ACCOUNT_ID)) {
            finalAccounts = [...finalAccounts, UNMANAGED_ACCOUNT];
        }
        return finalAccounts;
    }, [accounts, persons]);

    const stabilizedPersonIds = useMemo(() => {
        
        let sourceIds = allInvolvedIds;
        let destinationIds = allInvolvedIds;

        if (selectedPerson && selectedPerson.id && isSimpleReimbursement) {
            const selectedPersonId = selectedPerson.id;
            const foundOtherId = allInvolvedIds.find(id => id !== selectedPersonId);

            if (foundOtherId !== undefined) { 
                const otherPersonId = allInvolvedIds.find(id => id !== selectedPersonId);
                const isPayingOurDebtNet = totalAmount < 0; 
                
                if (isPayingOurDebtNet) {
                    sourceIds = [selectedPersonId];
                    destinationIds = [otherPersonId];
                } else {
                    sourceIds = [otherPersonId];
                    destinationIds = [selectedPersonId];
                }
            }
        } 
        return { sourceIds: [...sourceIds], destinationIds: [...destinationIds] }; 
    }, [selectedPerson, isSimpleReimbursement, allInvolvedIds, totalAmount]);
    
    const sourcePersonIds = stabilizedPersonIds.sourceIds;
    const destinationPersonIds = stabilizedPersonIds.destinationIds;


    // ✅ Stabilisation de la fonction de filtrage
    const getFilteredAccounts = useCallback((personIds) => {
        
        const involvedPersons = persons.filter(p => personIds.includes(p.id));
        const isUnmanagedInvolved = personIds.some(id => id < 0) || involvedPersons.some(p => p.is_managed === false || p.is_managed === 0);
        
        let filtered = stabilizedAllAccounts.filter(account => { 
            
            const isManagedAccountMatch = (
                personIds.includes(account.personId) && account.personId > 0
            );
            
            const isUnmanagedOptionMatch = (
                account.id === UNMANAGED_ACCOUNT_ID && isUnmanagedInvolved
            );
            
            return isManagedAccountMatch || isUnmanagedOptionMatch;
        });
        
        const uniqueAccounts = Array.from(new Map(filtered.map(acc => [acc.id, acc])).values());
        
        if (uniqueAccounts.length === 0 && isUnmanagedInvolved) {
            return [UNMANAGED_ACCOUNT];
        }

        return uniqueAccounts;
    }, [persons, stabilizedAllAccounts]); 

    // ✅ Stabilisation de la liste des comptes sources/destinations
    const sourceAccounts = useMemo(() => {
        return isBancaireMode ? getFilteredAccounts(sourcePersonIds) : [];
    }, [isBancaireMode, sourcePersonIds, getFilteredAccounts]);

    const destinationAccounts = useMemo(() => {
        return isBancaireMode ? getFilteredAccounts(destinationPersonIds) : [];
    }, [isBancaireMode, destinationPersonIds, getFilteredAccounts]);


    // 💡 Logique d'initialisation désensibilisée
    useEffect(() => {
        const setInitialId = (personIds, accountsList, setSelectedId) => {
            if (personIds.length === 1) {
                const personId = personIds[0];
                const personIsUnmanaged = personId < 0 || persons.find(p => p.id === personId)?.is_managed === false;
                
                if (personIsUnmanaged) { 
                    setSelectedId(UNMANAGED_ACCOUNT_ID);
                } else {
                    // 💡 PRIORITÉ : On regarde si le compte de la Topbar appartient à cette personne
                    const currentActiveAccount = accountsList.find(acc => acc.id === selectedAccount?.id);
                    
                    if (currentActiveAccount && currentActiveAccount.personId === personId) {
                        setSelectedId(currentActiveAccount.id);
                    } else {
                        // Sinon, premier compte géré de la personne
                        const defaultAccount = accountsList.find(acc => acc.personId === personId && acc.id !== UNMANAGED_ACCOUNT_ID);
                        setSelectedId(defaultAccount ? defaultAccount.id : null);
                    }
                }
            } else {
                setSelectedId(null);
            }
        };

        // On initialise seulement à l'ouverture du modal (show passant de false à true)
        if (show) {
            if (isBancaireMode) {
                setInitialId(sourcePersonIds, sourceAccounts, setSelectedSourceId);
                setInitialId(destinationPersonIds, destinationAccounts, setSelectedDestinationId);
            } else {
                setSelectedSourceId(null);
                setSelectedDestinationId(null);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, isBancaireMode]);

    const totalRemaining = Number(Math.abs(totalAmount).toFixed(2)); 

    const handleChange = (e) => {
        const { name, value } = e.target;
        setReimbursementData(p => ({ ...p, [name]: value }));
    };

    const handleAmountChange = (value) => {
        setReimbursementData(p => ({ ...p, amount: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 1. Validations de base
        const totalSelectedAmount = Number(reimbursementData.amount);
        const maxAllowed = Math.round(totalRemaining * 100) / 100;
        const inputAmount = Math.round(totalSelectedAmount * 100) / 100;

        if (inputAmount > maxAllowed + 0.01) {
            alert(`Action impossible : Le montant saisi (${formatCurrency(inputAmount)} €) est supérieur au montant attendu (${formatCurrency(maxAllowed)} €).`);
            return;
        }

        if (totalSelectedAmount <= 0) {
            alert("Veuillez saisir un montant valide.");
            return;
        }

        // 2. Validation des comptes si mode bancaire
        if (isBancaireMode && (!selectedSourceId || !selectedDestinationId)) {
            alert("Les comptes source et destination sont requis pour un remboursement bancaire.");
            return;
        }

        if (!window.confirm(`Confirmez-vous le remboursement de ${formatCurrency(totalSelectedAmount)} € ?`)) {
            return;
        }

        try {
            // Copie et tri des dettes pour appliquer le remboursement (priorité aux petits montants)
            const itemsCopy = JSON.parse(JSON.stringify(debtCreditItems));
            const sortedItems = itemsCopy.sort((a, b) => a.remaining_amount - b.remaining_amount);

            let remainingToApply = totalSelectedAmount;

            for (const item of sortedItems) {
                if (remainingToApply <= 0.001) break;

                let itemRemaining = Number(item.remaining_amount);
                if (itemRemaining <= 0) continue;

                // Calcul du montant à appliquer sur cette ligne précise
                let amountToApply = Math.min(remainingToApply, itemRemaining);
                amountToApply = parseFloat(amountToApply.toFixed(2));

                if (amountToApply < 0.01) continue;

                // 🔑 CONSTRUCTION DU PAYLOAD API
                // On utilise STRICTEMENT les sélecteurs de l'interface
                const body = {
                    debtCreditId: item.id,
                    userId: currentUser.id,
                    amount: amountToApply,
                    reimbursementMode: reimbursementData.reimbursementMode,
                    date: reimbursementData.date,
                    // Si mode bancaire, on prend les IDs sélectionnés, sinon on met -99
                    sourceAccountId: isBancaireMode ? selectedSourceId : UNMANAGED_ACCOUNT_ID,
                    destinationAccountId: isBancaireMode ? selectedDestinationId : UNMANAGED_ACCOUNT_ID,
                };

                const response = await authFetch.post(`/debts-credits/reimburse`, body);

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ error: response.statusText }));
                    throw new Error(`Échec du remboursement : ${errorData.error || 'Erreur inconnue'}`);
                }

                // Mise à jour des reliquats pour la suite de la boucle
                remainingToApply = parseFloat((remainingToApply - amountToApply).toFixed(2));
            }

            alert(`Remboursement enregistré avec succès.`);
            onClose(); // Ferme le modal et déclenche le rafraîchissement (via le parent)

        } catch (error) {
            console.error("❌ Erreur lors du remboursement:", error);
            alert(`Erreur: ${error.message}`);
        }
    };

    if (!show) return null;
    
    // Déterminer la paire Débiteur/Créancier pour le titre du modal
    let reimbursementTitle = "Enregistrer un remboursement";
    if (isSimpleReimbursement) {
        const firstItem = debtCreditItems[0];
        const debtor = persons.find(p => p.id === firstItem.debtor_person_id)?.name || 'Débiteur Inconnu';
        const creditor = persons.find(p => p.id === firstItem.creditor_person_id)?.name || 'Créancier Inconnu';
        reimbursementTitle = `Remboursement de ${debtor} vers ${creditor}`;
    } else {
        reimbursementTitle = `Remboursement Groupé (${debtCreditItems.length} lignes)`;
    }

    // Affichage d'une alerte si le montant net est négatif
    const isPayingOurDebt = totalAmount < 0; 
    const absTotalAmount = Math.abs(totalAmount);

    return (
        <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
                <h3>{reimbursementTitle}</h3>
                <p>Montant total dû pour la sélection : <strong>{formatCurrency(absTotalAmount)} €</strong></p>
                
                {absTotalAmount > 0 && (
                    <div style={styles.modalWarning}>
                        {isPayingOurDebt 
                            ? "Attention : vous remboursez la dette au Créancier."
                            : "Attention : vous recevez le remboursement du Débiteur."
                        }
                    </div>
                )}
                
                <form onSubmit={handleSubmit} style={styles.form}>
                    
                    {/* Montant */}
                    <label>Montant remboursé (max: {formatCurrency(totalRemaining)} €) :</label>
                    {isMultiSelection ? (
                        // Afficher le montant total, mais en texte simple (non modifiable)
                        <div style={{...styles.input, backgroundColor: '#eee', fontWeight: 'bold'}}>
                            {formatCurrency(totalRemaining)} € (Total forcé)
                        </div>
                    ) : (
                        <>
                            <CurrencyInput
                                name="amount"
                                value={reimbursementData.amount}
                                onChange={handleAmountChange}
                                style={{
                                    ...styles.input,
                                    borderColor: Number(reimbursementData.amount) > totalRemaining ? 'red' : '#ccc'
                                }}
                                required
                            />
                            {/* ⭐ Petit message d'erreur dynamique */}
                            {Number(reimbursementData.amount) > totalRemaining && (
                                <span style={{ color: 'red', fontSize: '0.8em', marginTop: '5px' }}>
                                    Le montant dépasse le maximum autorisé.
                                </span>
                            )}
                        </>
                    )}

                    {/* Date */}
                    <label>Date du remboursement :</label>
                    <input
                        type="date"
                        name="date"
                        value={reimbursementData.date}
                        onChange={handleChange}
                        required
                        style={styles.input}
                    />
                    
                    {/* Mode de remboursement */}
                    <label>Mode de remboursement :</label>
                    <select
                        name="reimbursementMode"
                        value={reimbursementData.reimbursementMode}
                        onChange={handleChange}
                        required
                        style={styles.select}
                    >
                        {PAYMENTS_MOD.map(m => (<option key={m} value={m}>{m}</option>))}
                    </select>

                    {/* Comptes (si mode bancaire) */}
                    {isBancaireMode ? (
                        <div style={styles.accountSelectorsContainer}>
                            {/* Compte Source (Débiteur) */}
                            <div style={styles.accountSelectorColumn}>
                                <AccountSelectorList 
                                    accounts={sourceAccounts}
                                    selectedId={selectedSourceId}
                                    onSelect={setSelectedSourceId}
                                    isSource={true}
                                    title="Compte Source (Débiteur)"
                                />
                            </div>

                            {/* Compte Destination (Créancier) */}
                            <div style={styles.accountSelectorColumn}>
                                <AccountSelectorList
                                    accounts={destinationAccounts}
                                    selectedId={selectedDestinationId}
                                    onSelect={setSelectedDestinationId}
                                    isSource={false}
                                    title="Compte Destination (Créancier)"
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={styles.modalWarning}>
                            Aucun compte n'est à renseigner car le mode de règlement ({reimbursementData.reimbursementMode}) est considéré comme ne transitant pas par un compte bancaire géré.
                        </div>
                    )}

                    <div style={styles.modalActions}>
                        <button type="button" style={styles.cancelButton} onClick={onClose}>
                            Annuler
                        </button>
                        <button type="submit" style={styles.reimburseButton}>
                            Enregistrer le Remboursement
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}